import type { OrganisationConfiguration } from "@thalamos/aspire-common";

import { canReconcileViaAspireFlag } from "../canReconcileViaAspireFlag.js";

/**
 * Determines if patient reconciliation is available in the S136 context.
 *
 * Reconciliation is possible when EITHER:
 * 1. The Vision flag (`patientReconciliationEnabled`) is on and a PDS org is
 *    available (from trust/POS context or user's own org), OR
 * 2. The Aspire flag (`reconciliationViaNhsNumberEnabled`) is on and the
 *    user's org has NHS number + PDS configured.
 *
 * When "Trust not listed" is toggled on, `hasPdsOrgFromContext` is false,
 * so only orgs with their own PDS connection can reconcile via the Vision path.
 */
export function canReconcile({
  patientReconciliationEnabled,
  sessionOrganisationConfiguration,
  patientHasNhsNumber,
  hasPdsOrgFromContext,
}: {
  patientReconciliationEnabled: boolean | undefined;
  sessionOrganisationConfiguration:
    | OrganisationConfiguration
    | null
    | undefined;
  patientHasNhsNumber: boolean;
  /** Whether getPdsOrgForReconciliation() found a PDS org from the trust/POS context */
  hasPdsOrgFromContext: boolean;
}): boolean {
  // Path 1: Aspire org-level flag (works in all contexts including S136)
  if (
    canReconcileViaAspireFlag({
      sessionOrganisationConfiguration,
      patientHasNhsNumber,
    })
  ) {
    return true;
  }

  // Path 2: Vision incident-level flag (S136 only, existing behaviour)
  if (!patientReconciliationEnabled) return false;
  if (patientHasNhsNumber) return false;

  // Trust/POS context provides a PDS org — any user (including guests) can reconcile
  if (hasPdsOrgFromContext) return true;

  // Fallback: user's own org must have a PDS connection
  const orgHasPdsConnection =
    sessionOrganisationConfiguration?.pdsEnabled === true &&
    !!sessionOrganisationConfiguration?.pdsEndUserOrganisationODSCode;

  return orgHasPdsConnection;
}
